import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { getFriendlyAuthErrorMessage } from "@/lib/authErrorMessages";
import { secureSignIn } from "@/lib/secureLogin";

interface InviteDetails {
  church_name: string;
  church_logo_url: string | null;
  role: string;
  email: string;
  expires_at: string;
}

interface FunctionInvokeErrorLike {
  message: string;
  context?: {
    json?: () => Promise<unknown>;
  };
}

interface ErrorBodyLike {
  error?: string;
}

export default function InvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  // State for the "complete setup" path: user arrived via Supabase invite email
  // (the invite link now points to this page with #access_token in the hash)
  const [inviteSession, setInviteSession] = useState<Session | null>(null);
  const [displayName, setDisplayName] = useState("");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSignIn, setIsSignIn] = useState(false);
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Detect auth links in URL hash (invite/recovery) and capture session so we can
  // continue account setup without forcing the user to restart the flow.
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hasAccessToken = !!hashParams.get("access_token");
    if (!hasAccessToken) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (sess) setInviteSession(sess);
    });
    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      if (existing) setInviteSession(existing);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!token) { setInvalid(true); setLoading(false); return; }
    const getInvitationDetailsRpc = "get_invitation_details" as unknown as Parameters<typeof supabase.rpc>[0];
    supabase.rpc(getInvitationDetailsRpc, { _token: token })
      .then(({ data, error }) => {
        if (error || !data || (Array.isArray(data) && data.length === 0)) {
          setInvalid(true);
        } else {
          const d = Array.isArray(data) ? data[0] : data;
          setInvite(d as InviteDetails);
        }
        setLoading(false);
      });
  }, [token]);

  // If the invited email already has an account (e.g. a re-invited revoked user),
  // default to the sign-in form instead of account creation.
  useEffect(() => {
    if (!invite?.email) return;
    supabase.functions
      .invoke("invite-user", {
        body: { email: invite.email, checkOnly: true, invitationToken: token },
      })
      .then(({ data }) => {
        if (data?.existingUser) {
          setIsSignIn(true);
          setSignInEmail(invite.email);
        }
      })
      .catch(() => { /* default to signup form */ });
  }, [invite?.email, token]);

  // Signup: name + password only (email comes from invite).
  // The invite email already proved address ownership, so invite-signup creates
  // the account pre-confirmed and joins the church — no confirmation email step.
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !invite) return;
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("invite-signup", {
        body: { token, password, display_name: username },
      });
      if (error) {
        let message = error.message;
          try {
            const invokeError = error as FunctionInvokeErrorLike;
            const body = await invokeError.context?.json?.();
            if (typeof body === "object" && body !== null && "error" in body) {
              const errorBody = body as ErrorBodyLike;
              if (typeof errorBody.error === "string") message = errorBody.error;
            }
        } catch { /* keep generic message */ }
        throw new Error(message);
      }
      if (data?.error) throw new Error(data.error);

      const { error: signInError } = await secureSignIn(invite.email, password);
      if (signInError) throw signInError;

      await queryClient.invalidateQueries({ queryKey: ["church_profile"] });
      await queryClient.invalidateQueries({ queryKey: ["user_role_church"] });
      await queryClient.invalidateQueries({ queryKey: ["subscription"] });
      toast.success(`Welcome! You've joined ${invite.church_name}`);
      navigate("/dashboard");
    } catch (err: unknown) {
      if (/already.*(registered|exists)/i.test(String(err?.message || ""))) {
        setIsSignIn(true);
        setSignInEmail(invite.email);
        toast.info("You already have an account. Sign in with your password to accept this invitation.");
      } else {
        toast.error(getFriendlyAuthErrorMessage(err, "Unable to create your account from this invitation."));
      }
    }
    setSubmitting(false);
  };

  // Recovery link redirects back here with a session, landing on the
  // complete-setup screen where they set a new password and join.
  const handleForgotPassword = async () => {
    const email = signInEmail.trim() || invite?.email;
    if (!email || !token) return;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/invite?token=${token}`,
    });
    if (error) {
      toast.error(getFriendlyAuthErrorMessage(error, "Failed to send reset email."));
    } else {
      toast.success("Password reset email sent. Check your inbox.");
    }
  };

  // Sign-in path: existing user accepting an invite
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !invite) return;
    setSubmitting(true);
    try {
      const { error } = await secureSignIn(signInEmail, signInPassword);
      if (error) throw error;

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const acceptInvitationRpc = "accept_invitation" as unknown as Parameters<typeof supabase.rpc>[0];
        const { error: rpcError } = await supabase.rpc(acceptInvitationRpc, {
          _token: token,
          _user_id: user.id,
        });
        if (rpcError) throw rpcError;
      }
      await queryClient.invalidateQueries({ queryKey: ["church_profile"] });
      await queryClient.invalidateQueries({ queryKey: ["user_role_church"] });
      await queryClient.invalidateQueries({ queryKey: ["subscription"] });
      toast.success(`Welcome! You've joined ${invite.church_name}`);
      navigate("/dashboard");
    } catch (err: unknown) {
      toast.error(getFriendlyAuthErrorMessage(err, "Unable to sign in and accept this invitation."));
    }
    setSubmitting(false);
  };

  // Complete-setup path: user arrived via Supabase invite email and already has a session.
  // They just need to set their name + password, then accept the invitation.
  const handleCompleteSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteSession?.user || !token || !invite) return;
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      // Set the user's password (they were created without one via invite)
      const { error: pwError } = await supabase.auth.updateUser({ password });
      if (pwError) throw pwError;

      // Update display name
      const name = displayName.trim();
      if (name) {
        await supabase.from("profiles").upsert(
          { user_id: inviteSession.user.id, display_name: name },
          { onConflict: "user_id" }
        );
        await supabase.auth.updateUser({ data: { display_name: name, full_name: name } });
      }

      // Accept the invitation (links user to church + assigns role)
      const acceptInvitationRpc = "accept_invitation" as unknown as Parameters<typeof supabase.rpc>[0];
      const { error: rpcError } = await supabase.rpc(acceptInvitationRpc, {
        _token: token,
        _user_id: inviteSession.user.id,
      });
      if (rpcError) throw rpcError;

      await queryClient.invalidateQueries({ queryKey: ["church_profile"] });
      await queryClient.invalidateQueries({ queryKey: ["user_role_church"] });
      await queryClient.invalidateQueries({ queryKey: ["subscription"] });
      toast.success(`Welcome! You've joined ${invite.church_name}`);
      navigate("/dashboard");
    } catch (err: unknown) {
      toast.error(getFriendlyAuthErrorMessage(err, "Failed to complete account setup."));
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen brand-page-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (invalid || !invite) {
    return (
      <div className="min-h-screen brand-page-bg flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
          <h1 className="text-xl font-heading font-bold text-foreground">Invalid Invitation</h1>
          <p className="text-muted-foreground mt-2">This invitation link is invalid or has expired.</p>
          <Button className="mt-6" onClick={() => navigate("/login")}>Go to Login</Button>
        </div>
      </div>
    );
  }

  // Complete-setup screen: user arrived via Supabase invite email link (already has session)
  if (inviteSession && invite) {
    return (
      <div className="min-h-screen brand-page-bg flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            {invite.church_logo_url ? (
              <img src={invite.church_logo_url} alt="" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4" />
            ) : (
              <img src="/ichurch-logo.svg" alt="iChurch Data logo" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4" />
            )}
            <h1 className="text-2xl font-heading font-bold text-foreground">{invite.church_name}</h1>
            <p className="text-muted-foreground mt-1">
              Complete your account to join as <Badge variant="secondary" className="ml-1">{invite.role}</Badge>
            </p>
          </div>
          <form onSubmit={handleCompleteSetup} className="stat-card space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={inviteSession.user.email || ""} readOnly className="bg-muted text-muted-foreground cursor-not-allowed" />
            </div>
            <div className="space-y-2">
              <Label>Your Name</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Enter your name" required />
            </div>
            <div className="space-y-2">
              <Label>Create Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} />
            </div>
            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" required minLength={6} />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Complete Setup &amp; Join
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen brand-page-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {invite.church_logo_url ? (
            <img src={invite.church_logo_url} alt="" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4" />
          ) : (
            <img src="/ichurch-logo.svg" alt="iChurch Data logo" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4" />
          )}
          <h1 className="text-2xl font-heading font-bold text-foreground">{invite.church_name}</h1>
          <p className="text-muted-foreground mt-1">
            You've been invited to join as <Badge variant="secondary" className="ml-1">{invite.role}</Badge>
          </p>
        </div>

        {!isSignIn ? (
          /* ── New account signup (primary path for invited staff) ── */
          <form onSubmit={handleSignUp} className="stat-card space-y-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Choose a username" onFocus={(e) => e.target.placeholder = ''} onBlur={(e) => e.target.placeholder = 'Choose a username'} required />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={invite.email} readOnly className="bg-muted text-muted-foreground cursor-not-allowed" />
            </div>
            <div className="space-y-2">
              <Label>Create Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" onFocus={(e) => e.target.placeholder = ''} onBlur={(e) => e.target.placeholder = '••••••••'} required minLength={6} />
            </div>
            <div className="space-y-2">
              <Label>Confirm Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" onFocus={(e) => e.target.placeholder = ''} onBlur={(e) => e.target.placeholder = '••••••••'} required minLength={6} />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Create Account &amp; Join
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <button type="button" onClick={() => setIsSignIn(true)} className="text-primary font-medium hover:underline">
                Sign in instead
              </button>
            </p>
          </form>
        ) : (
          /* ── Existing user sign-in ── */
          <form onSubmit={handleSignIn} className="stat-card space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Sign in with your existing account to accept this invitation.
            </p>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={signInEmail} onChange={(e) => setSignInEmail(e.target.value)} placeholder="your@email.com" onFocus={(e) => e.target.placeholder = ''} onBlur={(e) => e.target.placeholder = 'your@email.com'} required />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={signInPassword} onChange={(e) => setSignInPassword(e.target.value)} placeholder="••••••••" onFocus={(e) => e.target.placeholder = ''} onBlur={(e) => e.target.placeholder = '••••••••'} required />
              <div className="text-right">
                <button type="button" onClick={handleForgotPassword} className="text-xs text-primary hover:underline">
                  Forgot password?
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Sign In &amp; Join
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              New to iChurch Data?{" "}
              <button type="button" onClick={() => setIsSignIn(false)} className="text-primary font-medium hover:underline">
                Create account
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
